import _ from 'lodash';
import React, { useState } from 'react';
import { Field, setSubmitFailed } from 'redux-form';

import { Column } from 'admin/components/common/Grid';
import Button from 'admin/components/common/Button';
import Text from 'admin/components/common/Text';
import { TextArea, TextField } from 'admin/components/common/Form';
import { STORY_NAME_MAX_LEN } from 'admin/components/common/Form/validations';

import t from 'utils/translate';
import { useDidMount } from 'common/hooks/use-did-mount';
import { tid, TID } from 'common/utils/test-ids';
import type { AdminThunkDispatch } from 'admin/reducers';

import { fields } from './Form';
import WizardTitle from './WizardTitle';

const showErrorsOn = {
	name: (meta, input) => meta.submitFailed && !meta.active,
	contentPrompt: (meta, input) => meta.submitFailed && !meta.active,
};

const randomName = () => {
	const names = [
		'exciting',
		'thrilling',
		'impressive',
		'breathtaking',
		'fascinating',
		'notable',
		'phenomenal',
		'surprising',
		'wonderful',
		'unique',
	];
	return names[_.random(names.length - 1)];
};

type MainViewProps = {
	username?: string;
	onOk: () => Promise<void>;
	dispatch: AdminThunkDispatch;
	syncErrors?: { name?: string; contentPrompt?: string };
	form: string;
	isGenericContent: boolean;
};

const TemplateName: React.FC<MainViewProps> = ({ isGenericContent, syncErrors, dispatch, form, onOk, username }) => {
	const nameInputWrapRef = React.createRef<HTMLInputElement>();
	const [placeholder] = useState(`${username ? `${username}'s` : 'Your'} ${randomName()} story`);

	useDidMount(() => {
		if (nameInputWrapRef.current) {
			nameInputWrapRef.current.querySelector('input')?.focus();
		}
	});

	const onFormSubmit = React.useCallback(() => {
		const fieldName = isGenericContent ? fields.contentPrompt : fields.name;

		if (syncErrors?.[fieldName]) {
			dispatch(setSubmitFailed(form, fieldName));
		} else {
			onOk();
		}
	}, [isGenericContent, syncErrors, dispatch, form, onOk]);

	const renderInputField = () => {
		if (isGenericContent) {
			return (
				<>
					<WizardTitle>{t(`createStoryModal.fields.contentPrompt.label`)}</WizardTitle>
					<Field
						name={fields.contentPrompt}
						component={TextArea}
						autoSelect
						showErrorsOn={showErrorsOn.contentPrompt}
						autoComplete="off"
						placeholder={t(`createStoryModal.fields.contentPrompt.placeholder`)}
						isLabelUppercase={false}
						hint={t(`createStoryModal.fields.contentPrompt.description`)}
						rows={8}
						isRequired
					/>
				</>
			);
		}

		return (
			<>
				<WizardTitle>{t(`createStoryModal.fields.${fields.name}.label`)}</WizardTitle>
				<Field
					name={fields.name}
					component={TextField}
					autoSelect
					showErrorsOn={showErrorsOn.name}
					autoComplete="off"
					limit={{ max: STORY_NAME_MAX_LEN }}
					placeholder={placeholder}
					isLabelUppercase={false}
					isRequired
					view={TextField.view.hero}
					{...tid(TID['create-story-name-input'])}
				/>
			</>
		);
	};

	return (
		<div style={{ width: 800 }}>
			{renderInputField()}
			<br />
			<Column justifyContent="center">
				<Button
					type="submit"
					onClick={onFormSubmit}
					textSize={Text.size.label}
					{...tid(TID['create-story-submit-button'])}
				>
					{t(isGenericContent ? 'createStoryModal.generate' : 'createStoryModal.create')}
				</Button>
			</Column>
		</div>
	);
};

export default TemplateName;
